import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  getCanvasRenderData,
  getCanvasRenderDataV3,
  getConversionReport,
  getEditorState,
  getEditorStateV3,
  getReadiness,
  getReadinessV3,
  getTemplatePreviewRenderData,
  listCatalog,
  listCatalogV3,
  listRevisions,
  openTemplateDraft,
  publish,
  publishV3,
  restoreToDraft,
  saveSlot,
  saveSlots,
  saveSlotsV3,
  setInterfaceLocale,
  setInterfaceLocaleV3,
  stageTemplate,
  updateTemplateVersionV3,
  type WebsiteSlotsSaveResult,
} from '../services/website-templates-admin.service';
import { WebsiteTemplateServiceError } from '../services/website-templates-errors';
import type { WebsiteSlotBatch } from '../lib/website-templates/slots';
import {
  uploadWebsiteMedia,
  type UploadWebsiteMediaInput,
  type WebsiteMediaUploadError,
} from '../services/website-media-upload.service';
import type { SupabaseClient } from '../supabase/client';
import type {
  PublicStoreWebsiteDto,
  PublishV3Result,
  SaveSlotsV3Result,
  StoreWebsiteConversionReport,
  StoreWebsiteEditorState,
  StoreWebsiteEditorStateV3,
  UpdateTemplateVersionV3Result,
  StoreWebsiteRevisionPage,
  WebsiteCanvasRenderData,
  WebsiteDraftMutationResult,
  WebsiteInterfaceLocale,
  WebsiteMediaUploadResult,
  WebsitePageKind,
  WebsitePublishResult,
  WebsiteReadinessIssue,
  WebsiteReadinessReport,
  WebsiteSlotValue,
  WebsiteStageResult,
  WebsiteTemplateCatalogPage,
  WebsiteTemplateCatalogPageV3,
  WebsiteTemplateCategory,
} from '../types/website-templates';

export const websiteTemplatesAdminKeys = {
  editorState: (storeId: string) => ['website-editor-state', storeId] as const,
  catalog: (storeId: string, category: string, style: string) =>
    ['website-template-catalog', storeId, category, style] as const,
  catalogRoot: (storeId: string) => ['website-template-catalog', storeId] as const,
  templatePreview: (storeId: string, templateVersionId: string, pageKind: WebsitePageKind) =>
    ['website-template-preview', storeId, templateVersionId, pageKind] as const,
  canvas: (storeId: string, pageKind: WebsitePageKind) =>
    ['website-canvas-render-data', storeId, pageKind] as const,
  canvasRoot: (storeId: string) => ['website-canvas-render-data', storeId] as const,
  readiness: (storeId: string) => ['website-readiness', storeId] as const,
  revisions: (storeId: string) => ['website-revisions', storeId] as const,
  conversionReport: (storeId: string) => ['website-conversion-report', storeId] as const,
  /**
   * Epic #228 P3: one template's editor state. `null` is the entry-route read
   * (the template the store last opened). A child of `editorState(storeId)`.
   */
  editorStateV3: (storeId: string, templateId: string | null) =>
    ['website-editor-state', storeId, templateId] as const,
  /** Epic #228 P3: one template's canvas. A new root, so v1 canvas writes never touch it. */
  canvasV3: (storeId: string, templateId: string, pageKind: WebsitePageKind) =>
    ['website-canvas', storeId, templateId, pageKind] as const,
  canvasV3Template: (storeId: string, templateId: string) =>
    ['website-canvas', storeId, templateId] as const,
  readinessV3: (storeId: string, templateId: string) =>
    ['website-readiness', storeId, templateId] as const,
  catalogV3: (storeId: string, category: string, style: string) =>
    ['website-template-catalog', storeId, 'v3', category, style] as const,
};

/** Keep the write pending until active readers can supply the next draft's CAS base. */
async function invalidateDraft(queryClient: QueryClient, storeId: string): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: websiteTemplatesAdminKeys.editorState(storeId) }),
    queryClient.invalidateQueries({ queryKey: websiteTemplatesAdminKeys.canvasRoot(storeId) }),
    queryClient.invalidateQueries({ queryKey: websiteTemplatesAdminKeys.readiness(storeId) }),
    queryClient.invalidateQueries({ queryKey: websiteTemplatesAdminKeys.revisions(storeId) }),
    queryClient.invalidateQueries({ queryKey: websiteTemplatesAdminKeys.catalogRoot(storeId) }),
  ]);
}

/**
 * Epic #228 P3: a write to template X refreshes X only (its editor state,
 * canvas and readiness), the entry-route state, the catalog and the history.
 * It never refreshes another template's canvas. A publish changes which
 * template is live, so it refreshes every editor state of the store.
 */
async function invalidateTemplateDraft(
  queryClient: QueryClient,
  storeId: string,
  templateId: string,
  scope: 'template' | 'publish',
): Promise<void> {
  await Promise.all([
    scope === 'publish'
      ? queryClient.invalidateQueries({ queryKey: websiteTemplatesAdminKeys.editorState(storeId) })
      : Promise.all([
          queryClient.invalidateQueries({
            queryKey: websiteTemplatesAdminKeys.editorStateV3(storeId, templateId),
            exact: true,
          }),
          queryClient.invalidateQueries({
            queryKey: websiteTemplatesAdminKeys.editorStateV3(storeId, null),
            exact: true,
          }),
        ]),
    queryClient.invalidateQueries({
      queryKey: websiteTemplatesAdminKeys.canvasV3Template(storeId, templateId),
    }),
    queryClient.invalidateQueries({
      queryKey: websiteTemplatesAdminKeys.readinessV3(storeId, templateId),
      exact: true,
    }),
    queryClient.invalidateQueries({ queryKey: websiteTemplatesAdminKeys.revisions(storeId) }),
    queryClient.invalidateQueries({ queryKey: websiteTemplatesAdminKeys.catalogRoot(storeId) }),
  ]);
}

/**
 * Epic #228 P4: what a save of the auto-save queue refreshes once it has a
 * new head — exactly what a `useSaveWebsiteSlotsV3` success refreshes. The
 * queue owns the request, so the invalidation is exported for it.
 */
export function invalidateWebsiteTemplateDraft(
  queryClient: QueryClient,
  storeId: string,
  templateId: string,
): Promise<void> {
  return invalidateTemplateDraft(queryClient, storeId, templateId, 'template');
}

export interface WebsiteDraftWriteVariables {
  readonly expectedDraftRevisionId: string | null;
  readonly clientMutationId: string;
}

export function useWebsiteTemplateCatalog(
  client: SupabaseClient,
  args: {
    readonly storeId: string | null;
    readonly category?: WebsiteTemplateCategory | null;
    readonly style?: string | null;
    readonly supabaseUrl: string;
  },
): UseInfiniteQueryResult<InfiniteData<WebsiteTemplateCatalogPage, string | null>> {
  const storeId = args.storeId?.trim() ?? '';
  return useInfiniteQuery({
    queryKey: websiteTemplatesAdminKeys.catalog(
      storeId || 'pending',
      args.category ?? 'all',
      args.style ?? 'all',
    ),
    enabled: storeId !== '',
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      listCatalog(client, {
        storeId,
        category: args.category ?? null,
        style: args.style ?? null,
        cursor: pageParam,
        supabaseUrl: args.supabaseUrl,
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 60_000,
  });
}

export function useStoreWebsiteEditorState(
  client: SupabaseClient,
  storeId: string | null,
): UseQueryResult<StoreWebsiteEditorState> {
  const id = storeId?.trim() ?? '';
  return useQuery({
    queryKey: websiteTemplatesAdminKeys.editorState(id || 'pending'),
    enabled: id !== '',
    queryFn: () => getEditorState(client, id),
    staleTime: 0,
  });
}

export function useWebsiteTemplatePreviewRenderData(
  client: SupabaseClient,
  args: {
    readonly storeId: string | null;
    readonly templateVersionId: string | null;
    readonly pageKind: WebsitePageKind;
  },
): UseQueryResult<PublicStoreWebsiteDto> {
  const storeId = args.storeId?.trim() ?? '';
  const templateVersionId = args.templateVersionId?.trim() ?? '';
  return useQuery({
    queryKey: websiteTemplatesAdminKeys.templatePreview(
      storeId || 'pending',
      templateVersionId || 'pending',
      args.pageKind,
    ),
    enabled: storeId !== '' && templateVersionId !== '',
    queryFn: () =>
      getTemplatePreviewRenderData(client, {
        storeId,
        templateVersionId,
        pageKind: args.pageKind,
      }),
    staleTime: 60_000,
  });
}

export function useWebsiteCanvasRenderData(
  client: SupabaseClient,
  args: {
    readonly storeId: string | null;
    readonly pageKind: WebsitePageKind;
    readonly enabled?: boolean;
  },
): UseQueryResult<WebsiteCanvasRenderData> {
  const storeId = args.storeId?.trim() ?? '';
  return useQuery({
    queryKey: websiteTemplatesAdminKeys.canvas(storeId || 'pending', args.pageKind),
    enabled: storeId !== '' && args.enabled !== false,
    queryFn: () =>
      getCanvasRenderData(client, {
        storeId,
        pageKind: args.pageKind,
      }),
    staleTime: 0,
  });
}

export function useWebsiteReadiness(
  client: SupabaseClient,
  storeId: string | null,
): UseQueryResult<WebsiteReadinessIssue[]> {
  const id = storeId?.trim() ?? '';
  return useQuery({
    queryKey: websiteTemplatesAdminKeys.readiness(id || 'pending'),
    enabled: id !== '',
    queryFn: () => getReadiness(client, id),
    staleTime: 0,
  });
}

/** A25: ops and support only. No Admin UI lists or restores revisions. */
export function useStoreWebsiteRevisions(
  client: SupabaseClient,
  storeId: string | null,
): UseInfiniteQueryResult<InfiniteData<StoreWebsiteRevisionPage, string | null>> {
  const id = storeId?.trim() ?? '';
  return useInfiniteQuery({
    queryKey: websiteTemplatesAdminKeys.revisions(id || 'pending'),
    enabled: id !== '',
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => listRevisions(client, { storeId: id, cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 0,
  });
}

export function useStoreWebsiteConversionReport(
  client: SupabaseClient,
  storeId: string | null,
): UseQueryResult<StoreWebsiteConversionReport | null> {
  const id = storeId?.trim() ?? '';
  return useQuery({
    queryKey: websiteTemplatesAdminKeys.conversionReport(id || 'pending'),
    enabled: id !== '',
    queryFn: () => getConversionReport(client, id),
    staleTime: 5 * 60_000,
  });
}

/**
 * Uploads media for a slot. It changes no draft: the returned id becomes part
 * of the draft only when the slot is saved with it.
 */
export function useUploadWebsiteMedia(
  client: SupabaseClient,
  args: { readonly storeId: string; readonly supabaseUrl: string },
): UseMutationResult<
  WebsiteMediaUploadResult,
  WebsiteMediaUploadError,
  Omit<UploadWebsiteMediaInput, 'storeId' | 'supabaseUrl'>
> {
  return useMutation({
    mutationFn: (variables) =>
      uploadWebsiteMedia(client, {
        ...variables,
        storeId: args.storeId,
        supabaseUrl: args.supabaseUrl,
      }),
  });
}

export function useStageWebsiteTemplate(
  client: SupabaseClient,
  storeId: string,
): UseMutationResult<
  WebsiteStageResult,
  unknown,
  WebsiteDraftWriteVariables & { readonly templateVersionId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => stageTemplate(client, { storeId, ...variables }),
    onSettled: () => invalidateDraft(queryClient, storeId),
  });
}

/** @deprecated A21: use `useSaveWebsiteSlots`. Kept for scripts and tests. */
export function useSaveWebsiteSlot(
  client: SupabaseClient,
  storeId: string,
): UseMutationResult<
  WebsiteDraftMutationResult,
  unknown,
  WebsiteDraftWriteVariables & { readonly slotId: string; readonly value: WebsiteSlotValue | null }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => saveSlot(client, { storeId, ...variables }),
    onSettled: () => invalidateDraft(queryClient, storeId),
  });
}

/**
 * C4: one Save of one region dialog. Never retried by React Query: a retry is
 * the SAME user action, so the dialog repeats it with the same
 * `clientMutationId` and gets the original result.
 */
export function useSaveWebsiteSlots(
  client: SupabaseClient,
  storeId: string | null,
): UseMutationResult<
  WebsiteSlotsSaveResult,
  WebsiteTemplateServiceError,
  WebsiteDraftWriteVariables & { readonly values: WebsiteSlotBatch }
> {
  const queryClient = useQueryClient();
  const id = storeId?.trim() ?? '';
  return useMutation({
    mutationFn: (variables) => {
      // No store, no request: there is nothing a save could belong to.
      if (id === '') return Promise.reject(new WebsiteTemplateServiceError('unknown'));
      return saveSlots(client, { storeId: id, ...variables });
    },
    retry: false,
    onSettled: () => {
      if (id !== '') return invalidateDraft(queryClient, id);
    },
  });
}

export function useSetWebsiteInterfaceLocale(
  client: SupabaseClient,
  storeId: string,
): UseMutationResult<
  WebsiteDraftMutationResult,
  unknown,
  WebsiteDraftWriteVariables & { readonly locale: WebsiteInterfaceLocale }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => setInterfaceLocale(client, { storeId, ...variables }),
    onSettled: () => invalidateDraft(queryClient, storeId),
  });
}

export function usePublishStoreWebsite(
  client: SupabaseClient,
  storeId: string,
): UseMutationResult<
  WebsitePublishResult,
  unknown,
  { readonly expectedDraftRevisionId: string; readonly clientMutationId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => publish(client, { storeId, ...variables }),
    onSettled: () => invalidateDraft(queryClient, storeId),
  });
}

export function useRestoreStoreWebsiteRevision(
  client: SupabaseClient,
  storeId: string,
): UseMutationResult<
  WebsiteDraftMutationResult,
  unknown,
  WebsiteDraftWriteVariables & { readonly revisionId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => restoreToDraft(client, { storeId, ...variables }),
    onSettled: () => invalidateDraft(queryClient, storeId),
  });
}

// ---------------------------------------------------------------------------
// Epic #228 P3: per-template drafts. Every hook names its template; none reads
// the store's compatibility draft pointer.

export interface WebsiteTemplateDraftVariables {
  readonly templateId: string;
  readonly clientMutationId: string;
}

export interface WebsiteTemplateHeadWriteVariables extends WebsiteTemplateDraftVariables {
  readonly expectedHeadRevisionId: string;
}

export function useWebsiteTemplateCatalogV3(
  client: SupabaseClient,
  args: {
    readonly storeId: string | null;
    readonly category?: WebsiteTemplateCategory | null;
    readonly style?: string | null;
    readonly supabaseUrl: string;
  },
): UseInfiniteQueryResult<InfiniteData<WebsiteTemplateCatalogPageV3, string | null>> {
  const storeId = args.storeId?.trim() ?? '';
  return useInfiniteQuery({
    queryKey: websiteTemplatesAdminKeys.catalogV3(
      storeId || 'pending',
      args.category ?? 'all',
      args.style ?? 'all',
    ),
    enabled: storeId !== '',
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      listCatalogV3(client, {
        storeId,
        category: args.category ?? null,
        style: args.style ?? null,
        cursor: pageParam,
        supabaseUrl: args.supabaseUrl,
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 60_000,
  });
}

/** `templateId: null` is the entry-route read only. */
export function useStoreWebsiteEditorStateV3(
  client: SupabaseClient,
  storeId: string | null,
  templateId: string | null,
): UseQueryResult<StoreWebsiteEditorStateV3> {
  const id = storeId?.trim() ?? '';
  return useQuery({
    queryKey: websiteTemplatesAdminKeys.editorStateV3(id || 'pending', templateId),
    enabled: id !== '',
    queryFn: () => getEditorStateV3(client, id, templateId),
    staleTime: 0,
  });
}

export function useCanvasRenderDataV3(
  client: SupabaseClient,
  args: {
    readonly storeId: string | null;
    readonly templateId: string | null;
    readonly pageKind: WebsitePageKind;
    readonly enabled?: boolean;
  },
): UseQueryResult<WebsiteCanvasRenderData> {
  const storeId = args.storeId?.trim() ?? '';
  const templateId = args.templateId?.trim() ?? '';
  return useQuery({
    queryKey: websiteTemplatesAdminKeys.canvasV3(
      storeId || 'pending',
      templateId || 'pending',
      args.pageKind,
    ),
    enabled: storeId !== '' && templateId !== '' && args.enabled !== false,
    queryFn: () => getCanvasRenderDataV3(client, { storeId, templateId, pageKind: args.pageKind }),
    staleTime: 0,
  });
}

export function useWebsiteReadinessV3(
  client: SupabaseClient,
  storeId: string | null,
  templateId: string | null,
): UseQueryResult<WebsiteReadinessReport> {
  const id = storeId?.trim() ?? '';
  const template = templateId?.trim() ?? '';
  return useQuery({
    queryKey: websiteTemplatesAdminKeys.readinessV3(id || 'pending', template || 'pending'),
    enabled: id !== '' && template !== '',
    queryFn: () => getReadinessV3(client, id, template),
    staleTime: 0,
  });
}

export function useOpenTemplateDraft(
  client: SupabaseClient,
  storeId: string,
): UseMutationResult<StoreWebsiteEditorStateV3, unknown, WebsiteTemplateDraftVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => openTemplateDraft(client, { storeId, ...variables }),
    onSettled: (_data, _error, variables) =>
      invalidateTemplateDraft(queryClient, storeId, variables.templateId, 'template'),
  });
}

/**
 * One Save of one region dialog into the named template. Never retried by
 * React Query: a retry is the SAME user action, so the dialog repeats it with
 * the same `clientMutationId`.
 */
export function useSaveWebsiteSlotsV3(
  client: SupabaseClient,
  storeId: string | null,
): UseMutationResult<
  SaveSlotsV3Result,
  WebsiteTemplateServiceError,
  WebsiteTemplateDraftVariables & {
    readonly expectedHeadRevisionId: string | null;
    readonly values: WebsiteSlotBatch;
  }
> {
  const queryClient = useQueryClient();
  const id = storeId?.trim() ?? '';
  return useMutation({
    mutationFn: (variables) => {
      // No store, no request: there is nothing a save could belong to.
      if (id === '') return Promise.reject(new WebsiteTemplateServiceError('unknown'));
      return saveSlotsV3(client, { storeId: id, ...variables });
    },
    retry: false,
    onSettled: (_data, _error, variables) => {
      if (id !== '')
        return invalidateTemplateDraft(queryClient, id, variables.templateId, 'template');
    },
  });
}

export function useSetWebsiteInterfaceLocaleV3(
  client: SupabaseClient,
  storeId: string,
): UseMutationResult<
  SaveSlotsV3Result,
  unknown,
  WebsiteTemplateHeadWriteVariables & { readonly locale: WebsiteInterfaceLocale }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => setInterfaceLocaleV3(client, { storeId, ...variables }),
    onSettled: (_data, _error, variables) =>
      invalidateTemplateDraft(queryClient, storeId, variables.templateId, 'template'),
  });
}

export function usePublishWebsiteV3(
  client: SupabaseClient,
  storeId: string,
): UseMutationResult<PublishV3Result, unknown, WebsiteTemplateHeadWriteVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => publishV3(client, { storeId, ...variables }),
    onSettled: (_data, _error, variables) =>
      invalidateTemplateDraft(queryClient, storeId, variables.templateId, 'publish'),
  });
}

export function useUpdateTemplateVersion(
  client: SupabaseClient,
  storeId: string,
): UseMutationResult<UpdateTemplateVersionV3Result, unknown, WebsiteTemplateHeadWriteVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => updateTemplateVersionV3(client, { storeId, ...variables }),
    onSettled: (_data, _error, variables) =>
      invalidateTemplateDraft(queryClient, storeId, variables.templateId, 'template'),
  });
}
